import { MediaMatcher } from '@angular/cdk/layout';
import { Location } from '@angular/common';
import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { PktrAppMenuService } from '@prospektr/pktr-app-menu';
import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { PartialProperty } from 'src/app/_shared/interface/property.interface';
import { FavoriteService } from 'src/app/_shared/service/favorite.service';
import { UserState } from 'src/app/_shared/state/user.state';
import { WorkspaceState } from 'src/app/_shared/state/workspace.state';
import { AppState } from 'src/app/app.state';
import { PropertyFilterState } from 'src/app/property-filter/state/property-filter.state';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import { ValidatorsService } from 'src/app/shared/service/validators.service';
import { SearchApi } from 'src/app/workspace/api/search.api';
import { PropertyService } from 'src/app/workspace/service/property.service';
import { ListState } from 'src/app/workspace/state/list.state';
import { MapState } from 'src/app/workspace/state/map.state';
import { PropertyState } from 'src/app/workspace/state/property.state';
import { SearchState } from 'src/app/workspace/state/search.state';
import MESSAGE from 'src/assets/resource/strings.json';
import constants from 'src/constant.json';
import { environment } from 'src/environments/environment';
import { PropertyListSheetFacade } from '../property-list-sheet.facade';

@Component({
  selector: 'app-property-list-sheet',
  templateUrl: './property-list-sheet.component.html',
  styleUrls: ['./property-list-sheet.component.scss'],
})
export class PropertyListSheetComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('listPaginator', { static: false }) listPaginator: MatPaginator;
  @ViewChild('propertyListContainer') propertyListContainer: ElementRef;
  @Input() drawer;
  @Output() showOnMap = new EventEmitter<string>();
  @Output() drawerArrowStatus = new EventEmitter<boolean>();
  stringRes: any = MESSAGE;
  propertyList: Array<PartialProperty> = [];
  highLightedId;
  canViewMapControl: boolean;
  workSpaceView: string;
  openPropertyListSheet: boolean = true;
  listingImageBaseUrl = environment.listingImageBaseUrl;
  mobileQuery: MediaQueryList;
  noPropertyView: string;
  searchInProgress: boolean = false;
  totalPropertyCount: number = null;
  userType: string = '';
  companyName: string = '';
  uniqueMlsList: Array<string> = [];

  propertySortFields: Array<any> = [
    {
      displayValue: 'Newest Listings',
      paramValue: 'Lst_ListingContractDate',
      sortOrder: 'DESC',
    },
    {
      displayValue: 'Price (High to Low)',
      paramValue: 'Lst_ListPrice',
      sortOrder: 'DESC',
    },
    {
      displayValue: 'Price (Low to High)',
      paramValue: 'Lst_ListPrice',
      sortOrder: 'ASC',
    },
    {
      displayValue: 'Living Area (High to Low)',
      paramValue: 'LivingArea',
      sortOrder: 'DESC',
    },
    {
      displayValue: 'Lot Size (High to Low)',
      paramValue: 'LotSizeSquareFeet',
      sortOrder: 'DESC',
    },
  ];

  defaultSort: any = this.propertySortFields[1];

  private mobileQueryListener: () => void;

  // Subscription to unsubscribe on component destroy
  onDestroyNotifier$ = new Subject();

  constructor(
    private authorizeService: AuthorizeService,
    private cdr: ChangeDetectorRef,
    private favoriteService: FavoriteService,
    private mapState: MapState,
    private media: MediaMatcher,
    private workspaceState: WorkspaceState,
    public appState: AppState,
    public listState: ListState,
    public propertyListSheetFacade: PropertyListSheetFacade,
    public propertyState: PropertyState,
    private snackBar: MatSnackBar,
    private appRef: ApplicationRef,
    private propertyService: PropertyService,
    private userState: UserState,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private propertyFilterState: PropertyFilterState,
    private searchState: SearchState,
    private searchApi: SearchApi,
    private subscriptionService: SubscriptionPlanService,
    public _pktrAppMenuService: PktrAppMenuService,
    public breakpointService: ValidatorsService
  ) {
    this.mobileQuery = media.matchMedia('(max-width: 767.98px)');
    this.mobileQuery.addListener(this.mobileQueryListener);
  }

  ngOnInit() {
    // const queryParamsToRemove: string[] = []; // Query parameters to remove
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };
    // queryParamsToRemove.forEach((param) => delete queryParams[param]);
    this._router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: queryParams,
      // queryParamsHandling: 'merge',
    });
    this.workSpaceView = this.workspaceState.workspaceViewValue;
    this.canViewMapControl = this.authorizeService.canView('mapControl');
    this.userType = this.userState.userDetails?.userType;
    this.companyName = this.appState && this.appState.companyInfoValue.alias;
    this.propertyFilterState.propertySortFields$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      if (res) {
        const filteredFields = this.propertySortFields.filter(
          (field) => field.paramValue === res.sortField && field.sortOrder === res.sortOrder
        );
        if (filteredFields.length > 0) {
          this.defaultSort = filteredFields[0];
        } else if (this.companyName === 'opscan') {
          this.defaultSort = this.propertySortFields[0];
        } else {
          this.defaultSort = this.propertySortFields[1];
        }
      } else {
        this.defaultSort = this.companyName === 'opscan' ? this.propertySortFields[0] : this.propertySortFields[1];
      }
    });
  }

  ngAfterViewInit(): void {
    this.propertyState.paginationApiProgress$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.searchInProgress = res;
      this.cdr.detectChanges();
    });

    this.propertyState.noPropertyView$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      this.noPropertyView = res;
    });

    this.propertyState.propertyCount$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res) => {
      this.totalPropertyCount = res.totalRecord;
      this.propertyListContainer.nativeElement.scrollTop = 0;
      if (!this.propertyState.listPropertySetValue || !this.propertyState.listPropertySetValue.length)
        return (this.propertyList = null);
      if (this.listState.isPaginatedResultValue) {
        this.propertyList = this.propertyState.listPropertySetValue;
      } else {
        if (this.listPaginator?.pageIndex) this.listPaginator.pageIndex = 0;
        this.propertyList = this.getSortedAndPaginatedResult();
        this.totalPropertyCount = this.propertyState.listPropertySetValue.length;
      }
      // find the unique MLSBoards in the list of properties
      let mlsList = [...new Set(this.propertyList.map((item) => item.Lst_MlsBoard))];
      this.uniqueMlsList.length = 0;
      mlsList.forEach((item) => {
        this.uniqueMlsList.push(item);
      });
      if (Array.isArray(this.uniqueMlsList) && this.uniqueMlsList.includes('MREIS')) {
        this.sendEventToFooterComponent();
      }
    });

    this.listState.pagination$.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res) {
        let defaultSortConfig = this.propertySortFields.find((elem) => {
          return elem.paramValue == res.sortField && elem.sortOrder == res.sortOrder;
        });

        if (defaultSortConfig) {
          this.defaultSort = defaultSortConfig;
        }
      }
    });
  }
  sendEventToFooterComponent() {
    this.propertyService.sendEvent({ loadImage: true });
  }
  ngOnDestroy() {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  onPageChange(event: PageEvent) {
    this.propertyListContainer.nativeElement.scrollTop = 0;
    if (this.listState.isPaginatedResultValue) {
      this.listState.setPaginationValue({
        pageNumber: (this.listPaginator?.pageIndex || 0) + 1,
        pageSize: this.listPaginator?.pageSize | 20,
        sortFields: this.defaultSort.paramValue,
        sortOrder: this.defaultSort.sortOrder as 'ASC' | 'DESC',
      });
    } else {
      this.propertyList = this.getSortedAndPaginatedResult();
    }
    this.cdr.detectChanges();
  }

  onSortChange(event: MatSelectChange) {
    this.updateQueryParam(event.value);
    this.propertyListContainer.nativeElement.scrollTop = 0;
    if (this.listState.isPaginatedResultValue) {
      this.listState.setPaginationValue({
        pageNumber: 1,
        pageSize: this.listPaginator?.pageSize | 20,
        sortFields: this.defaultSort.paramValue,
        sortOrder: this.defaultSort.sortOrder as 'ASC' | 'DESC',
      });
    } else {
      this.propertyList = this.getSortedAndPaginatedResult();
    }

    const userLog = {
      ...this.searchState.searchAddressValue,
      filters: this.propertyFilterState.appliedFilter,
      type: 'filter',
      sortField: this.defaultSort.paramValue,
      sortOrder: this.defaultSort.sortOrder as 'ASC' | 'DESC',
    };
    this.searchApi.setSearch(userLog);
    this.cdr.detectChanges();
  }

  updateQueryParam(sortValue) {
    let updatedQueryParam = {
      sortField: sortValue.paramValue,
      sortOrder: sortValue.sortOrder,
    };
    this._router.navigate(['workspace'], {
      queryParams: updatedQueryParam,
      queryParamsHandling: 'merge',
    });
  }

  getSortedAndPaginatedResult() {
    if (this.propertyState.listPropertySetValue?.length) {
      const pageIndex: number = this.listPaginator?.pageIndex || 0;
      const pageSize: number = this.listPaginator?.pageSize || 20;
      const sortField: string = this.defaultSort.paramValue;
      const sortOrder: string = this.defaultSort.sortOrder;

      // Separate featured and non-featured
      const featuredList = this.propertyState.listPropertySetValue.filter((p) => p.isFeatured);
      const nonFeaturedList = this.propertyState.listPropertySetValue.filter((p) => !p.isFeatured);

      const sortFn = (a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];

        if (moment(aVal).isValid() && moment(bVal).isValid()) {
          return sortOrder === 'ASC' ? moment(aVal).diff(moment(bVal)) : moment(bVal).diff(moment(aVal));
        } else {
          return sortOrder === 'ASC' ? aVal - bVal : bVal - aVal;
        }
      };

      // Sort both groups
      // const sortedFeatured = featuredList.sort(sortFn);
      const sortedFeatured = featuredList;
      const sortedNonFeatured = nonFeaturedList.sort(sortFn);

      // Merge them with featured first
      const fullSortedList = [...sortedFeatured, ...sortedNonFeatured];

      // Paginate the combined list
      const paginatedList = fullSortedList.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

      return paginatedList;
    } else {
      return null;
    }
  }

  toggleCardView() {
    this.openPropertyListSheet = !this.openPropertyListSheet;
  }

  followProperty(property) {
    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.favorite.key)) return;

    if (this.mapState.activeInfoWindowValue) this.mapState.activeInfoWindowValue.close();
    if (!this.authorizeService.canAct('favorites') || !this.authorizeService.canAct('mapControl')) return;
    if (property.isFavorite && property.PMXPropertyId) {
      const inputParams = {
        PMXPropertyId: property.PMXPropertyId,
      };
      this.favoriteService
        .unFollowFavorite(inputParams)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe(
          ({ status }: any) => {
            this.openSnackBar('Removed from watchlist', 'snackbar-danger');
            this.appRef.tick();
            this.updatePropertyFavoriteStatus(property.PMXPropertyId, false);
            if (!status || status != 'success') this.updatePropertyFavoriteStatus(property.PMXPropertyId, true);
          },
          () => {
            this.updatePropertyFavoriteStatus(property.PMXPropertyId, true);
          }
        );
    } else if (property.StateOrProvince && property.PMXPropertyId) {
      const inputParams = {
        PMXPropertyIdList: [property.PMXPropertyId.toString()],
        GroupIds: this.propertyListSheetFacade.defaultFollowGroup,
        stateList: [property.StateOrProvince],
      };
      this.favoriteService
        .addFavorites(inputParams)
        .pipe(takeUntil(this.onDestroyNotifier$))
        .subscribe(
          ({ status }: any) => {
            this.openSnackBar('Added to watchlist', 'snackbar-success');
            this.appRef.tick();
            this.updatePropertyFavoriteStatus(property.PMXPropertyId, true);
          },
          () => {
            this.updatePropertyFavoriteStatus(property.PMXPropertyId, false);
          }
        );
    }
  }

  updatePropertyFavoriteStatus(pmxPropertyId, isFavorite: boolean) {
    this.propertyList.some((property, index) => {
      if (property.PMXPropertyId == pmxPropertyId) {
        this.propertyList[index].isFavorite = isFavorite;
        return true;
      }
    });
    this.cdr.detectChanges();
  }

  getDetails(property) {
    this.breakpointService
      .isMobileOrTablet()
      .pipe(takeUntil(this.onDestroyNotifier$))
      .subscribe((isMobileOrTablet) => {
        if (isMobileOrTablet) {
          this._pktrAppMenuService.close(true);
        }

      });
    property.logInput = true;
    this.propertyState.propertyDetailsSheetInput = property;
  }

  getMarkerOnMap(PMXPropertyId) {
    this.openPropertyListSheet = !this.openPropertyListSheet;
    this.showOnMap.emit(PMXPropertyId);
  }

  close() {
    this.drawer.opened = false;
    this.drawerArrowStatus.emit(false);
  }
  openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style],
        verticalPosition: 'top',
      });
    }
  }
}
